import {useCallback, useEffect, useState} from 'react';
import {useLocalization} from '@shopify/hydrogen';
import {useMemo} from 'react';
import type {
  Language,
  LanguageCode, // Language,
} from '@shopify/hydrogen/storefront-api-types';
import type {LocalizedSlugs} from '../../types';
import clsx from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {useAcceptLanguageStore} from '../../hooks/useAcceptLanguageStore';

/**
 * A client component that selects the appropriate language to display for products on a website
 */

type Props = {
  slugs?: LocalizedSlugs;
  onClick?: () => void;
};

export default function LanguageSelector({slugs}: Props) {
  const {isLoaded, storeLanguage} = useAcceptLanguageStore();

  // const shop = useShop();
  const {
    language: {isoCode},
  } = useLocalization();

  const currentLanguage = useMemo<{
    name: string;
    isoCode: LanguageCode;
  }>(() => {
    const regionNamesInEnglish = new Intl.DisplayNames(['en'], {
      type: 'language',
    });

    return {
      name: regionNamesInEnglish.of(isoCode)!,
      isoCode: isoCode as LanguageCode,
    };
  }, [isoCode]);

  const LanguageList = () => {
    const [languages, setLanguages] = useState<Language[]>([]);
    useEffect(() => {
      fetch('/api/languages')
        .then((response) => response.json())
        .then((json) => setLanguages(json));
    }, []);
    return languages;
  };

  const setLanguage = useCallback(
    (eventOrProp: React.MouseEvent<HTMLElement> | string) => {
      let newIsoCode;

      if (typeof eventOrProp === 'object') {
        newIsoCode = event?.target.value as LanguageCode;
      } else {
        newIsoCode = eventOrProp;
      }

      const currentPath = window.location.pathname;
      const arr = currentPath.split('/');

      const englishPath = slugs?.locales.translations.find(
        (obj) => obj.locale === 'en',
      )?.slug;
      const norwegianPath = slugs?.locales.translations.find(
        (obj) => obj.locale === 'no',
      )?.slug;
      let redirectPath;

      if (currentLanguage.isoCode !== newIsoCode) {
        // INTL to NO
        if (newIsoCode === 'NB' && norwegianPath) {
          const filteredArr = arr.filter((currentPath) => currentPath !== 'en');
          filteredArr.splice(-1, 1, norwegianPath);
          redirectPath = filteredArr.join('/');
        } else if (newIsoCode === 'NB' && !norwegianPath) {
          const filteredArr = arr.filter((currentPath) => currentPath !== 'en');
          filteredArr.splice(-1, 1, arr[arr.length - 1]);
          redirectPath = filteredArr.join('/');
        }

        // NO to EN
        if (newIsoCode !== 'NB' && englishPath) {
          const filteredArr = currentPath.split('/').slice(1);
          filteredArr[filteredArr.length - 1] = englishPath;

          const transformedArr = [, newIsoCode.toLowerCase(), ...filteredArr];
          redirectPath = transformedArr.join('/');
        } else if (newIsoCode !== 'NB' && !englishPath) {
          const filteredArr = currentPath.split('/').slice(1);
          filteredArr[filteredArr.length - 1] = arr[arr.length - 1];
          const transformedArr = [, newIsoCode.toLowerCase(), ...filteredArr];
          redirectPath = transformedArr.join('/');
        }
      } else {
        redirectPath = currentPath;
      }
      storeLanguage();
      window.location.href = redirectPath as string;
    },
    [currentLanguage, slugs?.locales],
  );

  // Auto Redirect
  useEffect(() => {
    const acceptLanguage = () => {
      let acceptLanguage = navigator.language.toUpperCase();
      if (acceptLanguage.includes('NB') || acceptLanguage.includes('NO')) {
        acceptLanguage = 'NB';
      } else {
        acceptLanguage = 'EN';
      }
      return acceptLanguage.toUpperCase();
    };
    isLoaded === false && setLanguage(acceptLanguage());
  }, []);

  // languages to array
  const languages = LanguageList();
  return (
    <AnimatePresence>
      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        exit={{opacity: 0}}
        className="flex gap-2"
      >
        {languages?.map((language) => (
          <button
            onClick={(event) => setLanguage(event)}
            key={language.isoCode}
            value={language.isoCode}
            className={clsx(
              language.isoCode === currentLanguage.isoCode ? '' : 'opacity-40',
              'transition-opacity ease-out hover:opacity-100',
            )}
          >
            {language.isoCode === 'NB' ? 'NO' : language.isoCode}
          </button>
        ))}
      </motion.div>
    </AnimatePresence>
  );
}
